import * as THREE from 'three';

/** Chase camera behind the duck, looking downstream along the river. */
export class CameraRig {
  private readonly desired = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly smoothLook = new THREE.Vector3();
  private dirX = 0;
  private dirZ = -1;

  constructor(private readonly camera: THREE.PerspectiveCamera) {}

  private lift = 0;
  private tunnel = 0;

  private compute(target: THREE.Vector3, portrait: boolean): void {
    // In the pipe the camera drops in low behind the duck.
    const t = this.tunnel;
    const back = (portrait ? 5.5 : 7.5) * (1 - t) + 4.2 * t;
    const height = 5.2 * (1 - t) + 1.3 * t;
    const ahead = portrait ? 6 : 9;
    this.desired.set(target.x - this.dirX * back, target.y + height + this.lift, target.z - this.dirZ * back);
    // Look slightly downhill so the next waterfall lip and what's below it stay in view.
    this.look.set(target.x + this.dirX * ahead, target.y - 0.8 * (1 - t) + 0.4 * t, target.z + this.dirZ * ahead);
  }

  snapTo(target: THREE.Vector3, dirX: number, dirZ: number): void {
    this.dirX = dirX;
    this.dirZ = dirZ;
    this.compute(target, this.camera.aspect < 1);
    this.camera.position.copy(this.desired);
    this.smoothLook.copy(this.look);
    this.camera.lookAt(this.smoothLook);
  }

  /** `extraFov` widens the view with speed and paddle kicks. */
  update(
    delta: number,
    target: THREE.Vector3,
    dirX: number,
    dirZ: number,
    lag: number,
    extraFov = 0,
    lift = 0,
    tunnel = 0,
  ): void {
    this.lift = lift;
    this.tunnel = tunnel;
    const turn = 1 - Math.exp(-delta * 1.5);
    this.dirX += (dirX - this.dirX) * turn;
    this.dirZ += (dirZ - this.dirZ) * turn;
    const len = Math.hypot(this.dirX, this.dirZ) || 1;
    this.dirX /= len;
    this.dirZ /= len;

    const portrait = this.camera.aspect < 1;
    this.camera.fov = (portrait ? 66 : 55) + extraFov;
    this.camera.updateProjectionMatrix();
    this.compute(target, portrait);
    const follow = 1 - Math.exp(-delta / Math.max(0.001, lag));
    this.camera.position.lerp(this.desired, follow);
    this.smoothLook.lerp(this.look, follow);
    this.camera.rotation.set(0, 0, 0);
    this.camera.lookAt(this.smoothLook);
  }
}
